import React, { useEffect, useState, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "../api"; 

// --- 1. Import your existing icons ---
import moneyIcon from "../assets/coin.png";       
import calendarIcon from "../assets/schedule.png";       
import calendarCheckIcon from "../assets/calender.png"; 
import printIcon from '../assets/print.png'; 

// --- 2. Theme Constants ---
const THEME = {
    bg: "#f0fdf9",
    sidebarBg: "#1c3334",
    accent: "#20c997", 
    cardBg: "#ffffff",
    textDark: "#2d3436",
    textLight: "#636e72",
    borderColor: "#e1e4e8",
    success: "#2ecc71",
    danger: "#e74c3c",
    warning: "#f39c12",
    info: "#3498db"
};

const DASHBOARD_API_BASE = "/vres/dashboard/project/"; 
const PROJECT_LIST_API = "/vres/projects";

// =============================================================================
// 3. Sorting & Table Logic
// =============================================================================

const useSortableData = (items, config = null) => {
  const [sortConfig, setSortConfig] = useState(config);

  const sortedItems = useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const getValue = (obj, key) => key.split('.').reduce((o, i) => (o && o[i] !== undefined ? o[i] : 'N/A'), obj);
        const aValue = getValue(a, sortConfig.key);
        const bValue = getValue(b, sortConfig.key);

        if (String(aValue).toLowerCase() < String(bValue).toLowerCase()) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (String(aValue).toLowerCase() > String(bValue).toLowerCase()) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
};

const SortableTable = ({ title, data, columns }) => {
  const { items, requestSort, sortConfig } = useSortableData(data);
  const [filterText, setFilterText] = useState('');

  if (!data || data.length === 0) return (
    <div style={styles.tableCard}>
      <div style={styles.tableHeader}>
          <h3>{title} (0)</h3>
      </div>
      <p style={{textAlign: 'center', color: '#999', padding: '20px'}}>No data available.</p>
    </div>
  );

  const getClassNamesFor = (name) => {
    if (!sortConfig) return;
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  const filteredItems = items.filter(item => 
    columns.some(col => {
      const key = col.key;
      let val = col.render ? col.render(item) : (key.includes('.') ? key.split('.').reduce((o, i) => (o ? o[i] : null), item) : item[key]);
      if (typeof val !== 'string' && val !== null && val !== undefined) {
        val = String(val.props?.children || val); 
      }
      return val && String(val).toLowerCase().includes(filterText.toLowerCase());
    })
  );

  return (
    <div style={styles.tableCard}>
      <div style={styles.tableHeader}>
        <h3>{title} ({filteredItems.length} of {data.length})</h3>
        <input
          type="text"
          placeholder="Search..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={styles.btnViewDetails} 
          className="no-print"
        />
      </div>
      
      <div style={{overflowX: 'auto'}}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map(col => (
                <th 
                  key={col.key} 
                  onClick={() => requestSort(col.key)}
                  style={{...styles.th, cursor: 'pointer'}}
                >
                  {col.label}
                  <span style={{ marginLeft: '5px', fontSize: '0.8em', color: '#ccc' }}>
                    {getClassNamesFor(col.key) === 'ascending' ? '▲' : getClassNamesFor(col.key) === 'descending' ? '▼' : '⇅'}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item, i) => (
              <tr key={item.id || item.beneficiaryId || i} style={styles.tr}>
                {columns.map(col => (
                  <td key={col.key} style={styles.td}>
                    {col.render ? col.render(item) : col.key.includes('.') 
                      ? col.key.split('.').reduce((o, i) => (o ? o[i] : 'N/A'), item) || 'N/A'
                      : item[col.key] || 'N/A'
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// =============================================================================
// 4. Helper Components
// =============================================================================

const ChartBox = ({ title, data, colors }) => (
  <div style={styles.chartCard} className="avoid-break">
    <h3 style={styles.chartTitle}>{title}</h3>
    <div style={styles.chartContainer}>
        {data && data.some(d => d.value > 0) ? (
        <ResponsiveContainer width="100%" height="100%">
            <PieChart>
            <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60} 
                outerRadius={85}
                paddingAngle={0}
                dataKey="value"
                labelLine={false}
            >
                {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
            </Pie>
            <Tooltip formatter={(value) => [value, 'Count']} contentStyle={{border: 'none', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)'}} />
            <Legend verticalAlign="bottom" height={36}/>
            </PieChart>
        </ResponsiveContainer>
        ) : (
            <p style={{marginTop: '100px', color: '#999'}}>No data available.</p>
        )}
    </div>
  </div>
);

const MetricCard = ({ title, value, unit, icon }) => (
  <div style={styles.metricCard} className="avoid-break">
    <div style={styles.metricIconWrapper}>
        {icon && <img src={icon} alt="icon" style={styles.metricIconImg} />}
    </div>
    <div style={styles.metricLabel}>{title}</div>
    <div style={styles.metricValue}>{value} {unit}</div>
  </div>
);

// =============================================================================
// 5. Main Dashboard Component
// =============================================================================

const ProjectDashboardPage = ({ currentUser }) => {
  
  const userAssignedProjects = useMemo(() => {
    return currentUser?.projects || [];
  }, [currentUser]);

  const role = currentUser?.role;
  const [allProjectsFromAPI, setAllProjectsFromAPI] = useState([]); 
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(currentUser?.selectedProjectId || "");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await api.get(PROJECT_LIST_API);
        setAllProjectsFromAPI(res.data);
      } catch (err) {
        setError("Failed to load project list: " + err.message);
      }
    };
    fetchProjects();
  }, []); 

  useEffect(() => {
    if (allProjectsFromAPI.length === 0) return;

    let projectsToDisplay = allProjectsFromAPI;

    if (role === 'PROJECT COORDINATOR' || role === 'OBSERVER') {
      const assignedProjectIds = userAssignedProjects.map(p => p.projectId || p.id);
      if (assignedProjectIds.length > 0) {
        projectsToDisplay = allProjectsFromAPI.filter(project => 
          assignedProjectIds.includes(project.projectId || project.id)
        );
      }
    } 

    setProjects(projectsToDisplay);

    const isSelectedValid = selectedProject && projectsToDisplay.find(p => (p.projectId || p.id).toString() === selectedProject.toString());

    if (!isSelectedValid) {
       if (projectsToDisplay.length === 1) {
          setSelectedProject(projectsToDisplay[0].projectId || projectsToDisplay[0].id);
       } else {
          setSelectedProject("");
       }
    }

  }, [allProjectsFromAPI, userAssignedProjects, role, selectedProject]);

  useEffect(() => {
    if (!selectedProject) {
      setDashboardData(null); 
      return;
    }

    const fetchDashboardDetails = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await api.get(`${DASHBOARD_API_BASE}${selectedProject}`);
        setDashboardData(res.data);
      } catch (err) {
        console.error("Dashboard Fetch Error:", err);
        setError(`Failed to load dashboard details for Project ID ${selectedProject}.`); 
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardDetails();
  }, [selectedProject]);

  const voucherChartData = useMemo(() => dashboardData ? [
    { name: "Available", value: (dashboardData.totalVouchers || 0) - (dashboardData.totalRedemptions || 0) },
    { name: "Redeemed", value: dashboardData.totalRedemptions || 0 },
  ] : [], [dashboardData]);

  const beneficiaryChartData = useMemo(() => dashboardData ? [
    { name: "Approved", value: dashboardData.approvedBeneficiaries || 0 },
    { name: "Pending", value: (dashboardData.totalBeneficiaries || 0) - (dashboardData.approvedBeneficiaries || 0) },
  ] : [], [dashboardData]);

  const projectVoucherDetails = useMemo(() => {
    return {
      points: dashboardData?.voucherPoints || 'N/A', 
      validFrom: 'N/A', 
      validTill: 'N/A',
    };
  }, [dashboardData]);

  // --- ✅ FIX: Simplified & Corrected Column Logic ---
  const beneficiaryColumns = useMemo(() => {
    return [
      { label: 'Name', key: 'name' },
      { label: 'Contact', key: 'phone' },   
      { 
        label: 'Voucher Status', 
        key: 'voucherStatus', 
        render: (item) => {
          const status = item.voucherStatus; 
          
          let style = styles.statusNotIssued;
          let displayText = status || "Not Issued";

          if (status === 'REDEEMED') {
             style = styles.statusRedeemed;
             displayText = "Redeemed";
          } else if (status === 'ISSUED' || status === 'ACTIVE') {
             style = styles.statusIssued;
             displayText = "Issued"; 
          }

          return <span style={style}>{displayText}</span>;
        }
      },
      { label: 'Maker', key: 'makerName', render: (item) => item.makerName || 'N/A' },
      { label: 'Checker', key: 'checkerName', render: (item) => item.checkerName || 'N/A' },
      { 
        label: 'Redeeming Vendor', 
        key: 'vendorName', 
        render: (item) => {
          // ✅ STRICT CHECK: Only show vendor name if status is REDEEMED
          if (item.voucherStatus === 'REDEEMED') {
              return item.vendorName || 'N/A';
          }
          return 'N/A';
        }
      },
      { 
          label: 'Redeemed On', 
          key: 'redeemedOn', 
          render: (item) => {
            // ✅ STRICT CHECK: Only show date if status is REDEEMED
            if (item.voucherStatus === 'REDEEMED') {
               return item.redeemedOn || 'N/A';
            }
            return 'N/A'; 
          }
      },
    ];
  }, [dashboardData]); 

  // --- Inline Style Injection ---
  const DashboardGlobalStyles = () => (
    <style>{`
        .metric-card:hover { transform: translateY(-2px); transition: transform 0.2s; }
        .btn-view-details:hover { background-color: ${THEME.bg}; border-color: ${THEME.sidebarBg}; }
        .btn-print:hover { opacity: 0.9; }
        tr:hover { background-color: #fcfcfc !important; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-thumb { background: #ccc; borderRadius: 4px; }

        @media print {
            @page { size: A4; margin: 10mm; }
            body, html, #root { height: auto !important; overflow: visible !important; background-color: white !important; }
            .no-print, .controlBar, .btn-print, button, input { display: none !important; }
            .dashboardWrapper, .contentWrapper { height: auto !important; overflow: visible !important; width: 100% !important; padding: 0 !important; margin: 0 !important; display: block !important; }
            .avoid-break, .metric-card, .chart-card { break-inside: avoid; page-break-inside: avoid; border: 1px solid #ddd !important; box-shadow: none !important; }
            div[style*="height: 100%"] { height: auto !important; display: block !important; }
            .tableCard { overflow: visible !important; height: auto !important; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
        }
    `}</style>
  );

  if (loading) return <p style={{ textAlign: "center", marginTop: "50px", fontSize: "1.2em", color: THEME.textLight }}>Loading dashboard data...</p>;
  if (error) return <p style={{ textAlign: "center", color: THEME.danger, marginTop: "50px" }}>{error}</p>;

  return (
    <div style={styles.pageBody}>
      <DashboardGlobalStyles />
      
      <div style={styles.dashboardWrapper} className="dashboardWrapper">
          
          {/* Control Bar */}
          <div style={styles.controlBar} className="no-print">
            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                <label style={{fontWeight:'600', fontSize:'0.85rem'}}>
                    {role === 'OBSERVER' ? 'Assigned Project:' : 'Select Project:'}
                </label>
                <select
                    style={styles.projectSelect}
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    disabled={role === 'OBSERVER' && projects.length === 1}
                >
                    <option value="">-- Choose a Project --</option>
                    {projects.map((proj) => (
                    <option key={proj.projectId || proj.id} value={proj.projectId || proj.id}>
                        {proj.title || proj.projectName}
                    </option>
                    ))}
                </select>
            </div>
            {selectedProject && (
                <button onClick={handlePrint} style={styles.btnPrint}>
                    <img src={printIcon} alt="Print" style={styles.printIcon} />
                    Print Report
                </button>
            )}
          </div>

          {!selectedProject ? (
             <div style={styles.initialStateCard}>
                <h2>Select Project</h2>
                <p>Please select a project from the dropdown above to view its dashboard and analytics.</p>
             </div>
          ) : !dashboardData ? (
             <p style={{textAlign:'center', padding:'40px'}}>No data found.</p>
          ) : (
             <>
                {/* Metrics Grid */}
                <div style={styles.metricsGrid}>
                    <MetricCard title="Voucher Points Value" value={projectVoucherDetails.points} unit="Points" icon={moneyIcon} />
                    <MetricCard title="Voucher Valid From" value={projectVoucherDetails.validFrom} unit="" icon={calendarIcon} />
                    <MetricCard title="Voucher Valid Till" value={projectVoucherDetails.validTill} unit="" icon={calendarCheckIcon} />
                </div>

                {/* Charts Grid */}
                <div style={styles.chartsGrid}>
                    <ChartBox 
                        title="Voucher Status (Redeemed vs Available)" 
                        data={voucherChartData} 
                        colors={[THEME.success, THEME.danger]} 
                    />
                    <ChartBox 
                        title="Beneficiary Approval Status (Appr vs Pending)" 
                        data={beneficiaryChartData} 
                        colors={[THEME.info, THEME.warning]} 
                    />
                </div>

                {/* Table Section */}
                <SortableTable 
                    title="Beneficiary Details" 
                    data={dashboardData.beneficiaryList || []} 
                    columns={beneficiaryColumns}
                />

                {/* Footer Disclaimer */}
                <div style={styles.footerDisclaimer}>
                    <span style={{marginRight:'10px', fontSize:'1.1em'}}>⚠️</span>
                    <span>This is a computer generated report for purpose of payment as proof of work done and nodal officer can pay as per escrow agreement.</span>
                </div>
             </>
          )}
      </div>
    </div>
  );
};

// =============================================================================
// 6. Styles
// =============================================================================

const styles = {
  pageBody: {
    backgroundColor: THEME.bg,
    color: THEME.textDark,
    fontFamily: "'Segoe UI', Roboto, sans-serif",
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  topHeader: {
    backgroundColor: 'white',
    padding: '10px 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${THEME.borderColor}`,
    height: '60px',
    flexShrink: 0,
  },
  topHeaderTitle: {
      fontSize: '1rem',
      color: THEME.textDark,
      margin: 0,
  },
  dashboardWrapper: {
    padding: '20px 30px',
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%',
    flexGrow: 1,
    overflowY: 'auto',
  },
  controlBar: {
      background: THEME.cardBg,
      padding: '10px 20px',
      borderRadius: '8px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      border: `1px solid ${THEME.borderColor}`,
  },
  projectSelect: {
      padding: '8px',
      border: `1px solid ${THEME.borderColor}`,
      borderRadius: '6px',
      width: '280px',
      outline: 'none',
      fontSize: '0.9rem',
      backgroundColor: 'white',
  },
  btnPrint: {
      backgroundColor: THEME.sidebarBg,
      color: 'white',
      padding: '8px 16px',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.85rem',
  },
  printIcon: {
      width: '16px',
      height: '16px',
      objectFit: 'contain',
      filter: 'brightness(0) invert(1)',
  },
  initialStateCard: {
      background: 'white',
      padding: '40px',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
      textAlign: 'center',
      maxWidth: '600px',
      margin: '40px auto',
      borderTop: `4px solid ${THEME.accent}`,
  },
  metricsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginBottom: '20px',
  },
  metricCard: {
      background: 'white',
      padding: '20px',
      borderRadius: '10px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
      border: `1px solid ${THEME.borderColor}`,
      textAlign: 'center',
  },
  metricIconWrapper: {
      marginBottom: '10px',
      display: 'flex',
      justifyContent: 'center',
  },
  metricIconImg: {
      width: '40px',
      height: '40px',
      objectFit: 'contain',
  },
  metricLabel: {
      fontSize: '0.9rem',
      color: THEME.textLight,
      marginBottom: '5px',
  },
  metricValue: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: THEME.textDark,
  },
  chartsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: '20px',
      marginBottom: '20px',
  },
  chartCard: {
      background: 'white',
      padding: '20px',
      borderRadius: '10px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
      border: `1px solid ${THEME.borderColor}`,
      textAlign: 'center',
  },
  chartTitle: {
      fontSize: '1rem',
      marginBottom: '15px',
      color: THEME.textDark,
  },
  chartContainer: {
      display: 'flex',
      justifyContent: 'center',
      minHeight: '250px',
      height: '250px'
  },
  tableCard: {
      background: 'white',
      borderRadius: '10px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
      border: `1px solid ${THEME.borderColor}`,
      overflow: 'hidden',
      marginBottom: '20px',
  },
  tableHeader: {
      padding: '15px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${THEME.borderColor}`,
  },
  btnViewDetails: {
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '0.8rem',
    border: `1px solid ${THEME.borderColor}`,
    outline: 'none',
  },
  table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.85rem',
  },
  th: {
      textAlign: 'left',
      padding: '12px 20px',
      backgroundColor: '#f8f9fa',
      color: THEME.textLight,
      borderBottom: `2px solid ${THEME.borderColor}`,
      fontWeight: '600',
  },
  td: {
      padding: '12px 20px',
      borderBottom: `1px solid ${THEME.borderColor}`,
      color: THEME.textDark,
      verticalAlign: 'middle',
  },
  tr: {
      backgroundColor: 'white'
  },
  statusIssued: { color: THEME.info, fontWeight: '600', textDecoration: 'none' },
  statusRedeemed: { color: THEME.danger, fontWeight: '600', textDecoration: 'none' },
  statusNotIssued: { color: THEME.textLight, fontWeight: '600', fontStyle: 'italic' },
  footerDisclaimer: {
      backgroundColor: '#fff3cd',
      color: '#856404',
      padding: '15px 20px',
      borderRadius: '8px',
      border: '1px solid #ffeeba',
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
  },
};

export default ProjectDashboardPage;